
import { defineComponent, reactive, ref, watchEffect } from 'vue';

import FilterItem from '@/components/FilterItem.vue';
import Pagination from '@/components/Pagination/index.vue';
import { filtersData, advancedFiltersData } from '@/data';
import { IFilter, IIssueCategory, IIssueType } from '@/types';
import { useTaxonomy, useClaims } from '@/hooks';
import { Filter, FILTER_KEY } from '@/hooks/useFilter';
import { injectOrThrow } from '@/utils';

export default defineComponent({
  name: 'Filters',
  components: {
    FilterItem,
    Pagination,
  },
  setup() {
    /**
     * Filter state; provided in Home.vue
     */
    const _filter: Filter = injectOrThrow(FILTER_KEY);

    const showAdvancedFilters = ref<boolean>(false);

    const {
      loading,
      categories,
      publicResolutions,
      suppliers,
      tags,
      severity,
      priorities,
      getCategoryTypes,
      getCategoryTypeIssues,
      error,
    } = useTaxonomy();
    const { pages } = useClaims();

    const dropdownOptions = reactive({
      claimCategoryOptions: categories,
      supplierOptions: suppliers,
      resolutionTypeOptions: publicResolutions,
      claimIssueCategoryOptions: [] as IIssueCategory[],
      claimIssueTypeOptions: [] as IIssueType[],
      claimDefectSeverityOptions: severity,
      claimPriorityOptions: priorities,
      claimTagOptions: tags,
      claimFlagOptions: [{ id: true, name: 'Flagged Only' }],
      claimProductQualityIssueOptions: [
        { id: true, name: 'Yes' },
        { id: false, name: 'No' },
      ],
    });

    // auto update dropdown options that have dependency
    // reset issueCategory and issueType if category is changed
    watchEffect(() => {
      if (_filter.filter.claimCategory) {
        dropdownOptions.claimIssueCategoryOptions = getCategoryTypes(
          parseInt(_filter.filter.claimCategory)
        );

        if (_filter.filter.claimIssueCategory) {
          dropdownOptions.claimIssueTypeOptions = getCategoryTypeIssues(
            parseInt(_filter.filter.claimCategory),
            parseInt(_filter.filter.claimIssueCategory)
          );
        } else {
          dropdownOptions.claimIssueTypeOptions = [];
          _filter.filter.claimIssueType = '';
        }
      } else {
        dropdownOptions.claimIssueCategoryOptions = [];
        dropdownOptions.claimIssueTypeOptions = [];
        _filter.filter.claimIssueCategory = '';
        _filter.filter.claimIssueType = '';
      }
    });

    const onFilterInput = (value: string, fieldName: string) => {
      _filter.updateFilter(fieldName as keyof IFilter, value);
    };

    const toggleAdvancedFilters = () => {
      showAdvancedFilters.value = !showAdvancedFilters.value;
    };

    const goToPage = (pageNumber: number) => {
      _filter.filter.page = pageNumber.toString();
    };

    return {
      filtersData,
      advancedFiltersData,
      filter: _filter.filter,
      dropdownOptions,
      showAdvancedFilters,
      toggleAdvancedFilters,
      goToPage,
      loading,
      error,
      pages,
      onFilterInput,
    };
  },
});
